import React, { useEffect, useState } from 'react';
import {
  IonRow,
  IonCol,
  IonButton,
  IonText
} from '@ionic/react';
import { Card, Text } from '@tremor/react';
import { extractNumber, formatAmount } from '../../../utils/numberFormat';
import { colorTheme } from '../../../utils/constants';

const SubscriptionPlans = ({
  subscriptionPeriod,
  setSubscriptionPeriod,
  annualPrice = 0,
  quarterlyPrice = 0,
  monthlyPrice = 0,
  annualDiscountPercentage = 0,
  quarterlyDiscountPercentage = 0
}) => {
  const cleanAnnualPrice = extractNumber(annualPrice);
  const cleanQuarterlyPrice = extractNumber(quarterlyPrice);
  const cleanMonthlyPrice = extractNumber(monthlyPrice);
  const cleanAnnualDiscountPercentage = extractNumber(annualDiscountPercentage);
  const cleanQuarterlyDiscountPercentage = extractNumber(quarterlyDiscountPercentage);
  const annualSavings = cleanMonthlyPrice * 12 - cleanAnnualPrice;
  const quarterlySavings = cleanMonthlyPrice * 3 - cleanQuarterlyPrice;

  const isAnnual = subscriptionPeriod === 'annual';
  const isQuarterly = subscriptionPeriod === 'quarterly';
  const isMonthly = subscriptionPeriod === 'monthly';

  const [isDarkTheme, setIsDarkTheme] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkTheme(mediaQuery.matches);

    const handler = (e) => setIsDarkTheme(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const getBorderColor = (selected) => {
    if (!selected) return 'transparent';
    return isDarkTheme ? colorTheme.primary[75] : colorTheme.primary[600];
  };

  const accentTextColor = "var(--ion-color-primary)";

  const badgeBaseStyle = {
    display: "inline-block",
    fontWeight: 800,
    fontSize: "1.05rem",
    lineHeight: 1.15,
    padding: "0.45rem 0.95rem",
    borderRadius: "999px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    backgroundColor: colorTheme.primary[500],
    color: "white",
    whiteSpace: "nowrap",
    minWidth: "fit-content",
  };


  // Shared styles for consistency
  const cardContainerStyle = {
    position: 'relative',
  };

  const badgeWrapperStyle = {
    position: "absolute",
    top: "-10px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 5,
  };

  const cardBaseStyle = {
    borderRadius: '10px',
    minHeight: '370px',
    padding: '1rem',
    paddingTop: '2rem',
    marginTop: '1rem',
    cursor: 'pointer',
  };

  const titleWrapperStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    marginTop: "14px",
    marginBottom: "1rem",
  };

  const mostPopularBadgeStyle = {
    display: "inline-block",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    fontSize: "0.9rem",
    fontWeight: 800,
    padding: "0.35rem 0.85rem",
    borderRadius: '14px',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    boxShadow: '0 3px 10px rgba(0,0,0,0.05)',
  };

  const planTitleStyle = {
    fontWeight: 800,
    fontSize: "1.3rem",
    color: accentTextColor,
    marginBottom: 0,
    marginTop: 0,
  };

  const amountLabelStyle = {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: "#4b5563",
    marginBottom: "0.25rem",
  };

  const amountValueStyle = {
    fontWeight: 800,
    fontSize: "1.3rem",
    color: accentTextColor,
  };

  const savingsBoxStyle = {
    padding: "12px",
    borderRadius: "12px",
    background: "linear-gradient(90deg, rgba(34,197,94,0.12), rgba(34,197,94,0.05))",
    border: "2px solid rgba(34,197,94,0.35)",
    marginBottom: "12px",
  };

  const savingsLabelStyle = {
    color: "#15803d",
    fontWeight: 700,
    fontSize: "0.95rem",
    marginBottom: "0.25rem",
  };

  const savingsValueStyle = {
    color: "#15803d",
    fontWeight: 800,
    fontSize: "1.3rem",
  };

  const amountSectionStyle = {
    marginBottom: "1rem",
  };

  return (
    <>
      <style>{`
        @media (min-width: 576px) and (max-width: 1024px) {
          .discount-badge {
            font-size: 0.9rem !important;
            padding: 0.4rem 0.8rem !important;
          }
        }
      `}</style>
      <IonRow className="ion-margin-top">
      {/* Annual Plan */}
      <IonCol size="12" sizeSm="4">
        <div style={cardContainerStyle}>
          <div style={badgeWrapperStyle}>
            <span className="discount-badge" style={badgeBaseStyle}>
              {cleanAnnualDiscountPercentage}% off
            </span>
          </div>

          <Card
            className="d-flex flex-column justify-content-between"
            style={{
              ...cardBaseStyle,
              border: `2px solid ${getBorderColor(isAnnual)}`,
            }}
            onClick={() => setSubscriptionPeriod('annual')}
          >
            <div className="ion-text-center">
              <div style={titleWrapperStyle}>
                <span style={mostPopularBadgeStyle}>
                  Most Popular
                </span>
                <h3 style={planTitleStyle}>
                  Annual
                </h3>
              </div>

              <div style={amountSectionStyle}>
                <div style={amountLabelStyle}>Amount</div>
                <div style={amountValueStyle}>
                  KES {formatAmount(cleanAnnualPrice)}
                </div>
              </div>

              <div style={savingsBoxStyle}>
                <div style={savingsLabelStyle}>You save</div>
                <div style={savingsValueStyle}>
                  KES {formatAmount(annualSavings)}
                </div>
              </div>
            </div>

            <IonButton
              expand="block"
              fill={'outline'}
              disabled={isAnnual}
              color={'primary'}
              style={{
                opacity: isAnnual ? 1 : 0.6,
                filter: isAnnual ? "none" : "grayscale(20%)",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSubscriptionPeriod('annual');
              }}
            >
              {isAnnual ? 'Selected Plan' : 'Choose Plan'}
            </IonButton>
          </Card>
        </div>
      </IonCol>

      {/* Quarterly Plan */}
      <IonCol size="12" sizeSm="4">
        <div style={cardContainerStyle}>
          <div style={badgeWrapperStyle}>
            <span className="discount-badge" style={badgeBaseStyle}>
              {cleanQuarterlyDiscountPercentage}% off
            </span>
          </div>

          <Card
            className="d-flex flex-column justify-content-between"
            style={{
              ...cardBaseStyle,
              border: `2px solid ${getBorderColor(isQuarterly)}`,
            }}
            onClick={() => setSubscriptionPeriod('quarterly')}
          >
            <div className="ion-text-center">
              <div style={titleWrapperStyle}>
                <div style={{ height: "2.5rem" }}></div>
                <h3 style={planTitleStyle}>
                  3-Month Plan
                </h3>
              </div>

              <div style={amountSectionStyle}>
                <div style={amountLabelStyle}>Amount</div>
                <div style={amountValueStyle}>
                  KES {formatAmount(cleanQuarterlyPrice)}
                </div>
              </div>

              <div style={savingsBoxStyle}>
                <div style={savingsLabelStyle}>You save</div>
                <div style={savingsValueStyle}>
                  KES {formatAmount(Math.max(quarterlySavings, 0))}
                </div>
              </div>
            </div>

            <IonButton
              expand="block"
              fill={'outline'}
              color="primary"
              disabled={isQuarterly}
              style={{
                opacity: isQuarterly ? 1 : 0.6,
                filter: isQuarterly ? "none" : "grayscale(20%)",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSubscriptionPeriod('quarterly');
              }}
            >
              {isQuarterly ? 'Selected Plan' : 'Choose Plan'}
            </IonButton>
          </Card>
        </div>
      </IonCol>

      {/* Monthly Plan */}
      <IonCol size="12" sizeSm="4">
        <div style={cardContainerStyle}>
          <div style={badgeWrapperStyle}>
            <span className="discount-badge" style={badgeBaseStyle}>
              0% off
            </span>
          </div>

          <Card
            className="d-flex flex-column justify-content-between"
            style={{
              ...cardBaseStyle,
              border: `2px solid ${getBorderColor(isMonthly)}`,
            }}
            onClick={() => setSubscriptionPeriod('monthly')}
          >
            <div className="ion-text-center">
              <div style={titleWrapperStyle}>
                <div style={{ height: "2.5rem" }}></div>
                <h3 style={planTitleStyle}>
                  Monthly
                </h3>
              </div>

              <div style={amountSectionStyle}>
                <div style={amountLabelStyle}>Amount</div>
                <div style={amountValueStyle}>
                  KES {formatAmount(cleanMonthlyPrice)}
                </div>
              </div>

              <div style={savingsBoxStyle}>
                <div style={savingsLabelStyle}>You save</div>
                <div style={savingsValueStyle}>
                  KES 0
                </div>
              </div>
            </div>

            <IonButton
              expand="block"
              fill={'outline'}
              color="primary"
              disabled={isMonthly}
              style={{
                opacity: isMonthly ? 1 : 0.6,
                filter: isMonthly ? "none" : "grayscale(20%)",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSubscriptionPeriod('monthly');
              }}
            >
              {isMonthly ? 'Selected Plan' : 'Choose Plan'}
            </IonButton>
          </Card>
        </div>
      </IonCol>
    </IonRow>
    </>
  );
};

export default SubscriptionPlans;
